/*
  Wishlist page controller.
  The shared Wishlist service keeps storage, toggle state, and cross-tab sync in
  one place. This page script only handles page-specific rendering, hydration,
  summary math, and fetching fresh Shopify product data.
*/
(function () {
  function bootWishlistPages(scope) {
    (scope || document).querySelectorAll('[data-wishlist-page-root]').forEach(function (wishlistRoot) {
      if (wishlistRoot.getAttribute('data-wishlist-page-ready') === 'true') {
        return;
      }

      wishlistRoot.setAttribute('data-wishlist-page-ready', 'true');
      initWishlistPage(wishlistRoot);
    });
  }

  function initWishlistPage(root) {
    var wishlistApi = window.TCGHive && window.TCGHive.Wishlist;
    var cartFeedback = window.TCGHiveCartFeedback || null;
    var requestCache = {};
    var renderedItems = [];
    var renderVersion = 0;
    var summaryColumn = root.querySelector('[data-wishlist-summary-column]');
    var mobileSummary = root.querySelector('[data-wishlist-mobile-summary]');
    var loadingBlock = root.querySelector('[data-wishlist-loading]');
    var emptyState = root.querySelector('[data-wishlist-empty]');
    var singlesGroup = root.querySelector('[data-wishlist-group-section="single"]');
    var sealedGroup = root.querySelector('[data-wishlist-group-section="sealed"]');
    var singlesList = root.querySelector('[data-wishlist-group-list="single"]');
    var sealedList = root.querySelector('[data-wishlist-group-list="sealed"]');
    var totalCountTargets = Array.prototype.slice.call(root.querySelectorAll('[data-wishlist-summary-total-count]'));
    var singlesTotalTargets = Array.prototype.slice.call(root.querySelectorAll('[data-wishlist-summary-singles-total]'));
    var sealedTotalTargets = Array.prototype.slice.call(root.querySelectorAll('[data-wishlist-summary-sealed-total]'));
    var estimatedTotalTargets = Array.prototype.slice.call(root.querySelectorAll('[data-wishlist-summary-estimated-total]'));
    var totalCountLabelTargets = Array.prototype.slice.call(root.querySelectorAll('[data-wishlist-summary-total-count-label]'));
    var bulkAddButtons = Array.prototype.slice.call(root.querySelectorAll('[data-wishlist-bulk-add]'));

    if (!wishlistApi) {
      return;
    }

    function getConfig(attributeName, fallback) {
      return root.getAttribute(attributeName) || fallback;
    }

    /*
      Shopify exposes a locale-aware root for Ajax requests. Use it first so
      English storefront requests become /en/products/... instead of malformed
      paths like /enproducts/...; keep data-shop-root only as a safe fallback.
    */
    function getShopRoot() {
      var shopifyRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? String(window.Shopify.routes.root)
        : '';
      var fallbackRoot;

      if (shopifyRoot !== '') {
        return shopifyRoot.charAt(shopifyRoot.length - 1) === '/'
          ? shopifyRoot
          : shopifyRoot + '/';
      }

      fallbackRoot = getConfig('data-shop-root', '/');

      if (!fallbackRoot) {
        return '/';
      }

      return fallbackRoot.charAt(fallbackRoot.length - 1) === '/'
        ? fallbackRoot
        : fallbackRoot + '/';
    }

    function safeString(value) {
      if (value === null || value === undefined) {
        return '';
      }

      return String(value).trim();
    }

    function safeNumber(value) {
      var parsed = Number(value);

      if (!Number.isFinite(parsed)) {
        return 0;
      }

      return parsed;
    }

    function readPositiveInteger(value) {
      var parsed = Number(value);

      if (!Number.isFinite(parsed) || parsed <= 0) {
        return null;
      }

      return Math.floor(parsed);
    }

    function escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    function stripHtml(value) {
      return safeString(value).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    }

    function truncateText(value, maxLength) {
      var text = safeString(value);

      if (text.length <= maxLength) {
        return text;
      }

      return text.slice(0, Math.max(0, maxLength - 3)).trim() + '...';
    }

    function replaceTokens(template, replacements) {
      var output = String(template || '');

      Object.keys(replacements).forEach(function (token) {
        output = output.split('[' + token + ']').join(String(replacements[token]));
      });

      return output;
    }

    function fetchCartStateForWishlist() {
      if (cartFeedback && typeof cartFeedback.getCartState === 'function') {
        return cartFeedback.getCartState().catch(function () {
          return null;
        });
      }

      if (cartFeedback && typeof cartFeedback.refreshCartCount === 'function') {
        return cartFeedback.refreshCartCount().catch(function () {
          return null;
        });
      }

      return Promise.resolve(null);
    }

    function buildCartQuantityMap(cartState) {
      var quantityMap = {};

      if (!cartState || !Array.isArray(cartState.items)) {
        return quantityMap;
      }

      cartState.items.forEach(function (cartItem) {
        var variantId = safeString(cartItem && cartItem.variant_id);
        var quantity = safeNumber(cartItem && cartItem.quantity);

        if (variantId === '' || quantity <= 0) {
          return;
        }

        quantityMap[variantId] = (quantityMap[variantId] || 0) + quantity;
      });

      return quantityMap;
    }

    /*
      Shared setters keep the desktop summary card and the mobile sticky summary
      perfectly in sync without duplicating business logic.
    */
    function setTextContent(targets, value) {
      targets.forEach(function (target) {
        target.textContent = value;
      });
    }

    function setButtonDisabledState(targets, disabled) {
      targets.forEach(function (button) {
        button.disabled = disabled;
      });
    }

    function normalizeLanguageCode(value) {
      var normalized = safeString(value).toUpperCase();

      if (normalized === '') {
        return '';
      }

      if (normalized.indexOf('DE') === 0 || normalized.indexOf('GERMAN') === 0 || normalized.indexOf('DEUTSCH') === 0) {
        return 'DE';
      }

      if (normalized.indexOf('EN') === 0 || normalized.indexOf('ENG') === 0 || normalized.indexOf('ENGLISH') === 0) {
        return 'EN';
      }

      if (normalized.indexOf('JP') === 0 || normalized.indexOf('JA') === 0 || normalized.indexOf('JAPAN') === 0) {
        return 'JP';
      }

      return normalized.slice(0, 2);
    }

    function formatMoneyFromMinorUnits(valueInMinorUnits) {
      var locale = document.documentElement && document.documentElement.lang
        ? document.documentElement.lang
        : 'de';
      var currencyCode = getConfig('data-currency-code', 'EUR');
      var amount = safeNumber(valueInMinorUnits) / 100;

      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currencyCode
        }).format(amount);
      } catch (error) {
        return amount.toFixed(2) + ' ' + currencyCode;
      }
    }

    function itemCountLabel(count) {
      var singularLabel = getConfig('data-text-item-singular', 'Artikel');
      var pluralLabel = getConfig('data-text-item-plural', 'Artikel');

      return count + ' ' + (count === 1 ? singularLabel : pluralLabel);
    }

    function getOriginalSealedLabel() {
      return getConfig('data-text-original-sealed', 'Originalversiegelt');
    }

    function getLanguageFromProductTags(product) {
      var rawTags = product && product.tags ? product.tags : [];
      var tags = Array.isArray(rawTags) ? rawTags : safeString(rawTags).split(',');
      var resolvedLanguage = '';

      tags.some(function (tag) {
        var tagText = safeString(tag);
        var tagParts = tagText.split(':');
        var tagKey = safeString(tagParts[0]).toLowerCase();
        var tagValue = safeString(tagParts.slice(1).join(':'));

        if ((tagKey === 'language' || tagKey === 'lang') && tagValue !== '') {
          resolvedLanguage = normalizeLanguageCode(tagValue) || tagValue;
          return true;
        }

        return false;
      });

      return resolvedLanguage;
    }

    function buildFlagMarkup(languageCode, label) {
      var normalizedCode = normalizeLanguageCode(languageCode);
      var assetUrl = '';

      if (normalizedCode === 'DE') {
        assetUrl = getConfig('data-flag-de-url', '');
      } else if (normalizedCode === 'EN') {
        assetUrl = getConfig('data-flag-en-url', '');
      } else if (normalizedCode === 'JP') {
        assetUrl = getConfig('data-flag-jp-url', '');
      }

      if (assetUrl !== '') {
        return (
          '<span class="tcghive-wishlist__flag" aria-label="' + escapeHtml(label || normalizedCode) + '">' +
            '<img src="' + escapeHtml(assetUrl) + '" alt="' + escapeHtml(label || normalizedCode) + '">' +
          '</span>'
        );
      }

      return '';
    }

    function buildVariantSummary(language, condition, finish) {
      var parts = [];

      if (safeString(language) !== '') {
        parts.push(safeString(language));
      }

      if (safeString(condition) !== '') {
        parts.push(safeString(condition));
      }

      if (safeString(finish) !== '') {
        parts.push(safeString(finish));
      }

      return parts.join(' \u00b7 ');
    }

    function getProductRequestUrl(handle) {
      return getShopRoot() + 'products/' + encodeURIComponent(handle) + '.js';
    }

    /*
      Request caching keeps repeated items from the same product from hammering
      Shopify's Product Ajax API more than once.
    */
    function fetchProductByHandle(handle) {
      if (requestCache[handle]) {
        return requestCache[handle];
      }

      requestCache[handle] = fetch(getProductRequestUrl(handle), {
        method: 'GET',
        headers: {
          Accept: 'application/json'
        },
        credentials: 'same-origin'
      }).then(function (response) {
        if (!response.ok) {
          throw new Error('product-fetch-' + response.status);
        }

        return response.json();
      });

      return requestCache[handle];
    }

    /*
      A small concurrency cap prevents an unusually large Wishlist from firing
      a burst of uncontrolled product requests all at once.
    */
    function fetchProductsByHandle(handles) {
      var normalizedHandles = handles.filter(function (handle) {
        return safeString(handle) !== '';
      });
      var uniqueHandles = normalizedHandles.filter(function (handle, index) {
        return normalizedHandles.indexOf(handle) === index;
      });
      var concurrency = Math.min(4, uniqueHandles.length);
      var nextIndex = 0;
      var results = {};
      var workers = [];

      function createWorker() {
        return new Promise(function (resolveWorker) {
          function runNext() {
            var handle = uniqueHandles[nextIndex];

            if (!handle) {
              resolveWorker();
              return;
            }

            nextIndex += 1;

            fetchProductByHandle(handle)
              .then(function (product) {
                results[handle] = {
                  status: 'fulfilled',
                  value: product
                };
              })
              .catch(function (error) {
                results[handle] = {
                  status: 'rejected',
                  reason: error
                };
              })
              .finally(runNext);
          }

          runNext();
        });
      }

      if (!uniqueHandles.length) {
        return Promise.resolve(results);
      }

      while (workers.length < concurrency) {
        workers.push(createWorker());
      }

      return Promise.all(workers).then(function () {
        return results;
      });
    }

    function getVariantOptionValue(product, variant, groupKey, fallbackValue) {
      var optionNames = Array.isArray(product.options) ? product.options : [];
      var optionValues = Array.isArray(variant.options) ? variant.options : [];
      var resolvedValue = '';

      optionNames.forEach(function (optionName, optionIndex) {
        var normalizedOptionName = safeString(optionName).toLowerCase();
        var candidateValue = safeString(optionValues[optionIndex]);

        if (candidateValue === '' || resolvedValue !== '') {
          return;
        }

        if (groupKey === 'language' && normalizedOptionName === 'language') {
          resolvedValue = candidateValue;
        } else if (groupKey === 'condition' && normalizedOptionName === 'condition') {
          resolvedValue = candidateValue;
        } else if (
          groupKey === 'finish' &&
          (
            normalizedOptionName.indexOf('finish') !== -1 ||
            normalizedOptionName.indexOf('foil') !== -1 ||
            normalizedOptionName.indexOf('printing') !== -1
          )
        ) {
          resolvedValue = candidateValue;
        } else if (
          groupKey === 'type' &&
          (
            normalizedOptionName === 'type' ||
            normalizedOptionName.indexOf('product type') !== -1
          )
        ) {
          resolvedValue = candidateValue;
        }
      });

      if (resolvedValue !== '') {
        return resolvedValue;
      }

      return safeString(fallbackValue);
    }

    function resolveProductUrl(product, variantId, includeVariant) {
      var productUrl = safeString(product && product.url);

      if (productUrl === '' && safeString(product && product.handle) !== '') {
        productUrl = getShopRoot() + 'products/' + safeString(product.handle);
      }

      if (productUrl !== '' && includeVariant && safeString(variantId) !== '') {
        return productUrl + '?variant=' + safeString(variantId);
      }

      return productUrl;
    }

    function resolveImageUrl(product, variant, savedItem) {
      if (variant && variant.featured_image && variant.featured_image.src) {
        return variant.featured_image.src;
      }

      if (product && product.featured_image) {
        return product.featured_image;
      }

      if (Array.isArray(product && product.images) && product.images.length > 0) {
        return product.images[0];
      }

      return safeString(savedItem.image);
    }

    function getVariantCartMaxQuantity(savedItem, variant) {
      var savedMaxQuantity = readPositiveInteger(savedItem && savedItem.cartMaxQuantity);
      var variantInventory = readPositiveInteger(
        variant && (variant.inventory_quantity || variant.inventoryQuantity || variant.inventoryCount)
      );
      var variantQuantityRuleMax = readPositiveInteger(variant && variant.quantity_rule && variant.quantity_rule.max);
      var variantPolicy = safeString(variant && (variant.inventory_policy || variant.inventoryPolicy)).toLowerCase();
      var variantManagement = safeString(variant && (variant.inventory_management || variant.inventoryManagement));
      var maxQuantity = savedMaxQuantity;

      /*
        Product Ajax normally does not expose private inventory counts, but if
        Shopify does provide a tracked deny-policy count here it is fresher than
        the locally saved hint from the original wishlist button.
      */
      if (variantManagement !== '' && variantPolicy !== 'continue' && variantInventory !== null) {
        maxQuantity = variantInventory;
      }

      if (variantQuantityRuleMax !== null) {
        maxQuantity = maxQuantity === null ? variantQuantityRuleMax : Math.min(maxQuantity, variantQuantityRuleMax);
      }

      return maxQuantity;
    }

    function applyCartStateToItems(items, cartState) {
      var cartQuantityMap = buildCartQuantityMap(cartState);

      return items.map(function (item) {
        var maxQuantity = readPositiveInteger(item.maxQuantity);
        var cartQuantity = safeNumber(cartQuantityMap[safeString(item.variantId)]);
        var catalogAvailable = item.catalogAvailable === true || (item.status === 'available' && item.available === true);
        var remainingQuantity = null;
        var canAddToCart = catalogAvailable;
        var nextItem = Object.assign({}, item, {
          catalogAvailable: catalogAvailable,
          cartQuantity: cartQuantity,
          maxQuantity: maxQuantity
        });

        if (catalogAvailable && maxQuantity !== null) {
          remainingQuantity = Math.max(maxQuantity - cartQuantity, 0);
          canAddToCart = remainingQuantity > 0;
        }

        nextItem.remainingQuantity = remainingQuantity;
        nextItem.canAddToCart = canAddToCart;

        if (catalogAvailable && canAddToCart) {
          nextItem.status = 'available';
          nextItem.message = getConfig('data-text-in-stock', 'Auf Lager');
        } else if (catalogAvailable && maxQuantity !== null && remainingQuantity === 0) {
          nextItem.status = 'cart_max_reached';
          nextItem.message = getConfig('data-text-cart-max-reached', 'Maximale Menge im Warenkorb');
        }

        return nextItem;
      });
    }

    function sanitizeSetName(value, fallbackKind) {
      var normalized = safeString(value);
      var lowerValue = normalized.toLowerCase();

      if (normalized === '' || lowerValue === 'my store' || lowerValue === 'tcghive') {
        if (fallbackKind === 'sealed') {
          return '';
        }

        return '';
      }

      return normalized;
    }

    function buildPreviewText(savedItem, product, kind) {
      var freshDescription = stripHtml(product && product.description);

      if (kind === 'single' && freshDescription !== '') {
        return truncateText(freshDescription, 180);
      }

      if (kind === 'sealed' && freshDescription !== '') {
        return truncateText(freshDescription, 180);
      }

      return truncateText(savedItem.previewText, 180);
    }

    function resolveLiveItem(savedItem, product) {
      var variant = Array.isArray(product.variants)
        ? product.variants.find(function (productVariant) {
            return String(productVariant.id) === String(savedItem.variantId);
          })
        : null;
      var kind = savedItem.kind === 'sealed' ? 'sealed' : 'single';
      var language = '';
      var condition = '';
      var finish = '';
      var previewText = '';
      var setName = '';
      var productUrl = '';
      var typeLabel = '';

      if (!variant) {
        language = safeString(savedItem.language);

        if (kind === 'sealed') {
          if (language === '') {
            language = getLanguageFromProductTags(product);
          }

          condition = getOriginalSealedLabel();
          finish = '';
        } else {
          condition = safeString(savedItem.condition);
          finish = safeString(savedItem.finish);
        }

        return {
          key: savedItem.key,
          productId: safeString(product.id),
          variantId: safeString(savedItem.variantId),
          handle: safeString(savedItem.handle),
          kind: kind,
          title: safeString(product.title) || savedItem.title || savedItem.handle,
          productUrl: resolveProductUrl(product, '', false) || savedItem.productUrl,
          image: resolveImageUrl(product, null, savedItem),
          setName: sanitizeSetName(savedItem.setName, kind),
          rarity: safeString(savedItem.rarity),
          previewText: buildPreviewText(savedItem, product, kind),
          language: language,
          condition: condition,
          finish: finish,
          typeLabel: safeString(savedItem.selectedOptions && savedItem.selectedOptions.Type),
          status: 'missing_variant',
          available: false,
          maxQuantity: readPositiveInteger(savedItem.cartMaxQuantity),
          priceMinor: 0,
          priceResolved: false,
          variantSummary: buildVariantSummary(language, condition, finish),
          message: getConfig('data-text-saved-variant-unavailable', 'Gespeicherte Version nicht mehr verf\u00fcgbar')
        };
      }

      language = getVariantOptionValue(product, variant, 'language', savedItem.language);
      condition = getVariantOptionValue(product, variant, 'condition', savedItem.condition);
      finish = getVariantOptionValue(product, variant, 'finish', savedItem.finish);
      typeLabel = getVariantOptionValue(product, variant, 'type', savedItem.selectedOptions && savedItem.selectedOptions.Type);
      previewText = buildPreviewText(savedItem, product, kind);
      setName = sanitizeSetName(savedItem.setName, kind);
      productUrl = resolveProductUrl(product, variant.id, true);

      if (setName === '' && kind === 'sealed') {
        setName = sanitizeSetName(product.vendor, kind);
      }

      if (kind === 'sealed') {
        if (safeString(language) === '') {
          language = getLanguageFromProductTags(product);
        }

        condition = getOriginalSealedLabel();
        finish = '';
      }

      return {
        key: savedItem.key,
        productId: safeString(product.id),
        variantId: safeString(variant.id),
        handle: safeString(product.handle) || savedItem.handle,
        kind: kind,
        title: safeString(product.title) || savedItem.title || savedItem.handle,
        productUrl: productUrl,
        image: resolveImageUrl(product, variant, savedItem),
        setName: setName,
        rarity: safeString(savedItem.rarity),
        previewText: previewText,
        language: language,
        condition: condition,
        finish: finish,
        typeLabel: typeLabel,
        status: variant.available ? 'available' : 'unavailable',
        available: !!variant.available,
        maxQuantity: getVariantCartMaxQuantity(savedItem, variant),
        priceMinor: safeNumber(variant.price),
        priceResolved: true,
        variantSummary: buildVariantSummary(language, condition, finish),
        message: variant.available
          ? getConfig('data-text-in-stock', 'Auf Lager')
          : getConfig('data-text-sold-out', 'Nicht auf Lager')
      };
    }

    function resolveUnavailableItem(savedItem, reason) {
      var kind = savedItem.kind === 'sealed' ? 'sealed' : 'single';
      var condition = kind === 'sealed' ? getOriginalSealedLabel() : safeString(savedItem.condition);
      var finish = kind === 'sealed' ? '' : safeString(savedItem.finish);

      return {
        key: safeString(savedItem.key),
        productId: safeString(savedItem.productId),
        variantId: safeString(savedItem.variantId),
        handle: safeString(savedItem.handle),
        kind: kind,
        title: safeString(savedItem.title) || safeString(savedItem.handle),
        productUrl: safeString(savedItem.productUrl),
        image: safeString(savedItem.image),
        setName: safeString(savedItem.setName),
        rarity: safeString(savedItem.rarity),
        previewText: safeString(savedItem.previewText),
        language: safeString(savedItem.language),
        condition: condition,
        finish: finish,
        typeLabel: safeString(savedItem.selectedOptions && savedItem.selectedOptions.Type),
        status: reason,
        available: false,
        maxQuantity: readPositiveInteger(savedItem.cartMaxQuantity),
        priceMinor: 0,
        priceResolved: false,
        variantSummary: buildVariantSummary(savedItem.language, condition, finish),
        message: getConfig('data-text-product-unavailable', 'Produkt derzeit nicht verf\u00fcgbar')
      };
    }

    function renderEntryToplineBadge(item) {
      if (item.kind === 'single' && safeString(item.rarity) !== '') {
        return '<span class="tcghive-wishlist__entry-rarity">' + escapeHtml(item.rarity) + '</span>';
      }

      if (item.kind === 'sealed' && safeString(item.typeLabel) !== '') {
        return '<span class="tcghive-wishlist__entry-status-chip">' + escapeHtml(item.typeLabel) + '</span>';
      }

      if (item.kind === 'sealed') {
        return '<span class="tcghive-wishlist__entry-status-chip">Sealed</span>';
      }

      return '';
    }

    function renderEntryPriceNote(item) {
      if (item.kind === 'single' && safeString(item.finish) !== '') {
        return escapeHtml(item.finish);
      }

      if (item.kind === 'sealed' && safeString(item.language) !== '') {
        return escapeHtml(item.language);
      }

      return '';
    }

    function renderEntryStatusGroups(item) {
      var groups = [];
      var flagMarkup = buildFlagMarkup(item.language, item.language);
      var variantPieces = [];
      var availabilityLabel = item.status === 'cart_max_reached'
        ? getConfig('data-text-cart-max-reached', 'Maximale Menge im Warenkorb')
        : item.status === 'available'
          ? getConfig('data-text-in-stock', 'Auf Lager')
          : getConfig('data-text-sold-out', 'Nicht auf Lager');

      if (item.kind === 'single' && safeString(item.condition) !== '') {
        groups.push(
          '<div class="tcghive-wishlist__entry-status-group">' +
            '<span class="material-symbols-outlined" aria-hidden="true">verified</span>' +
            '<span class="tcghive-body-copy tcghive-wishlist__entry-status-copy">' + escapeHtml(item.condition) + '</span>' +
          '</div>'
        );
      }

      if (item.kind === 'sealed') {
        groups.push(
          '<div class="tcghive-wishlist__entry-status-group">' +
            '<span class="material-symbols-outlined" aria-hidden="true">verified</span>' +
            '<span class="tcghive-body-copy tcghive-wishlist__entry-status-copy">' + escapeHtml(getOriginalSealedLabel()) + '</span>' +
          '</div>'
        );
      }

      if (safeString(item.language) !== '') {
        variantPieces.push(safeString(item.language));
      }

      if (item.kind === 'single' && safeString(item.finish) !== '') {
        variantPieces.push(safeString(item.finish));
      }

      if (variantPieces.length) {
        groups.push(
          '<div class="tcghive-wishlist__entry-status-group">' +
            (flagMarkup !== '' ? flagMarkup : '<span class="material-symbols-outlined" aria-hidden="true">inventory_2</span>') +
            '<span class="tcghive-body-copy tcghive-wishlist__entry-status-copy tcghive-wishlist__entry-status-copy--subtle">' + escapeHtml(variantPieces.join(' \u00b7 ')) + '</span>' +
          '</div>'
        );
      }

      if (item.status === 'cart_max_reached') {
        groups.push(
          '<div class="tcghive-wishlist__entry-status-group">' +
            '<span class="material-symbols-outlined" aria-hidden="true">shopping_cart_checkout</span>' +
            '<span class="tcghive-body-copy tcghive-wishlist__entry-status-copy tcghive-wishlist__entry-status-copy--subtle">' + escapeHtml(availabilityLabel) + '</span>' +
          '</div>'
        );
      } else {
        if (!variantPieces.length) {
          groups.push(
            '<div class="tcghive-wishlist__entry-status-group">' +
              '<span class="material-symbols-outlined" aria-hidden="true">inventory_2</span>' +
              '<span class="tcghive-body-copy tcghive-wishlist__entry-status-copy tcghive-wishlist__entry-status-copy--subtle">' + escapeHtml(availabilityLabel) + '</span>' +
            '</div>'
          );
        }
      }

      return groups.join('');
    }

    function renderEntryPrimaryAction(item) {
      var primaryLabel = getConfig('data-text-add-to-cart', 'In den Warenkorb');
      var unavailableLabel = getConfig('data-text-unavailable', 'Nicht verf\u00fcgbar');
      var cartMaxLabel = getConfig('data-text-cart-max-button', 'Bereits maximal im Warenkorb');
      var productTitle = escapeHtml(item.title);
      var variantSummary = escapeHtml(item.variantSummary);
      var maxQuantity = readPositiveInteger(item.maxQuantity);
      var cartMaxAttribute = maxQuantity === null ? '' : 'data-cart-max-quantity="' + escapeHtml(maxQuantity) + '" ';

      if (item.canAddToCart === true) {
        return (
          '<form class="tcghive-wishlist__entry-form" method="post" action="/cart/add" ' +
            'data-tcghive-cart-form ' +
            cartMaxAttribute +
            'data-product-title="' + productTitle + '" ' +
            'data-variant-summary="' + variantSummary + '">' +
            '<input type="hidden" name="id" value="' + escapeHtml(item.variantId) + '">' +
            '<input type="hidden" name="quantity" value="1">' +
            '<button class="tcghive-wishlist__entry-primary" type="submit">' +
              '<span>' + escapeHtml(primaryLabel) + '</span>' +
            '</button>' +
          '</form>'
        );
      }

      return (
        '<button class="tcghive-wishlist__entry-primary" type="button" disabled>' +
          '<span>' + escapeHtml(item.status === 'cart_max_reached' ? cartMaxLabel : unavailableLabel) + '</span>' +
        '</button>'
      );
    }

    function renderEntrySecondaryAction(item) {
      var viewLabel = getConfig('data-text-view-product', 'Produkt ansehen');

      if (item.status === 'available' || item.status === 'cart_max_reached' || safeString(item.productUrl) === '') {
        return '';
      }

      return (
        '<a class="tcghive-wishlist__entry-secondary" href="' + escapeHtml(item.productUrl) + '">' +
          '<span>' + escapeHtml(viewLabel) + '</span>' +
        '</a>'
      );
    }

    function renderRemoveButton(item) {
      return (
        '<button ' +
          'class="tcghive-wishlist__entry-remove" ' +
          'type="button" ' +
          'data-wishlist-remove ' +
          'data-wishlist-remove-variant-id="' + escapeHtml(item.variantId) + '" ' +
          'aria-label="' + escapeHtml(getConfig('data-text-remove', 'Von der Merkliste entfernen')) + '">' +
          '<span class="material-symbols-outlined" aria-hidden="true">delete</span>' +
        '</button>'
      );
    }

    function renderWishlistEntry(item) {
      var entryClasses = ['tcghive-wishlist__entry'];
      var priceText = item.priceResolved ? formatMoneyFromMinorUnits(item.priceMinor) : '--';
      var priceNote = renderEntryPriceNote(item);
      var statusMessageClass = 'tcghive-wishlist__entry-message';
      var mediaModifierClass = item.kind === 'sealed'
        ? 'tcghive-wishlist__entry-media tcghive-wishlist__entry-media--sealed'
        : 'tcghive-wishlist__entry-media tcghive-wishlist__entry-media--single';
      var previewMarkup = '';
      var secondaryActionMarkup = renderEntrySecondaryAction(item);
      var toplineBadgeMarkup = renderEntryToplineBadge(item);
      var statusMessageMarkup = '';

      if (item.status !== 'available' && item.status !== 'cart_max_reached') {
        entryClasses.push('tcghive-wishlist__entry--unavailable');
      }

      if (item.status === 'missing_variant') {
        statusMessageClass += ' tcghive-wishlist__entry-message--warning';
      } else if (item.status === 'missing_product') {
        statusMessageClass += ' tcghive-wishlist__entry-message--error';
      }

      if (safeString(item.previewText) !== '') {
        previewMarkup = '<p class="tcghive-wishlist__entry-preview tcghive-body-copy">' + escapeHtml(item.previewText) + '</p>';
      }

      if (item.status !== 'available' && item.status !== 'cart_max_reached') {
        statusMessageMarkup = '<div class="' + statusMessageClass + ' tcghive-body-copy">' + escapeHtml(item.message) + '</div>';
      }

      return (
        '<article class="' + entryClasses.join(' ') + '">' +
          '<a class="' + mediaModifierClass + '" href="' + escapeHtml(item.productUrl || '#') + '">' +
            (item.image
              ? '<img src="' + escapeHtml(item.image) + '" alt="' + escapeHtml(item.title) + '">'
              : ''
            ) +
          '</a>' +
          '<div class="tcghive-wishlist__entry-body">' +
            '<div class="tcghive-wishlist__entry-top">' +
              '<div class="tcghive-wishlist__entry-main">' +
                '<div class="tcghive-wishlist__entry-topline">' +
                  toplineBadgeMarkup +
                  (safeString(item.setName) !== '' ? '<span class="tcghive-wishlist__entry-eyebrow">' + escapeHtml(item.setName) + '</span>' : '') +
                '</div>' +
                '<div class="tcghive-wishlist__entry-title tcghive-product-title">' +
                  '<a href="' + escapeHtml(item.productUrl || '#') + '">' + escapeHtml(item.title) + '</a>' +
                '</div>' +
                previewMarkup +
              '</div>' +
              '<div class="tcghive-wishlist__entry-price-stack">' +
                '<span class="tcghive-wishlist__entry-price tcghive-price">' + escapeHtml(priceText) + '</span>' +
                (priceNote !== '' ? '<span class="tcghive-wishlist__entry-price-note">' + priceNote + '</span>' : '') +
              '</div>' +
            '</div>' +
            '<div class="tcghive-wishlist__entry-footer">' +
              '<div class="tcghive-wishlist__entry-status">' +
                '<div class="tcghive-wishlist__entry-status-row">' + renderEntryStatusGroups(item) + '</div>' +
                statusMessageMarkup +
              '</div>' +
              '<div class="tcghive-wishlist__entry-actions">' +
                renderRemoveButton(item) +
                renderEntryPrimaryAction(item) +
                secondaryActionMarkup +
              '</div>' +
            '</div>' +
          '</div>' +
        '</article>'
      );
    }

    function setAccordionState(triggerButton, isExpanded) {
      var controlsId = triggerButton.getAttribute('aria-controls');
      var controlledPanel = controlsId ? document.getElementById(controlsId) : null;

      if (!controlledPanel) {
        return;
      }

      triggerButton.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
      controlledPanel.classList.toggle('is-open', isExpanded);

      if (isExpanded) {
        controlledPanel.hidden = false;
        controlledPanel.style.maxHeight = controlledPanel.scrollHeight + 'px';
      } else {
        controlledPanel.style.maxHeight = '0px';
        window.setTimeout(function () {
          if (triggerButton.getAttribute('aria-expanded') === 'false') {
            controlledPanel.hidden = true;
          }
        }, 240);
      }
    }

    function updateGroupMeta(sectionElement, groupItems) {
      var groupKey = sectionElement.getAttribute('data-wishlist-group-section');
      var metaTarget = sectionElement.querySelector('[data-wishlist-group-meta="' + groupKey + '"]');
      var priceTotalMinor = groupItems.reduce(function (runningTotal, item) {
        return runningTotal + (item.priceResolved ? safeNumber(item.priceMinor) : 0);
      }, 0);
      var metaText = itemCountLabel(groupItems.length) + ' \u2022 ' + getConfig('data-text-estimated-total', 'Gesch\u00e4tzter Gesamtwert') + ': ' + formatMoneyFromMinorUnits(priceTotalMinor);

      if (metaTarget) {
        metaTarget.textContent = metaText;
      }
    }

    function updateSummary(items) {
      var totalItems = items.length;
      var singlesTotalMinor = 0;
      var sealedTotalMinor = 0;
      var availableItems = 0;

      items.forEach(function (item) {
        if (item.canAddToCart === true) {
          availableItems += 1;
        }

        if (!item.priceResolved) {
          return;
        }

        if (item.kind === 'sealed') {
          sealedTotalMinor += safeNumber(item.priceMinor);
        } else {
          singlesTotalMinor += safeNumber(item.priceMinor);
        }
      });

      setTextContent(totalCountTargets, String(totalItems));
      setTextContent(singlesTotalTargets, formatMoneyFromMinorUnits(singlesTotalMinor));
      setTextContent(sealedTotalTargets, formatMoneyFromMinorUnits(sealedTotalMinor));
      setTextContent(estimatedTotalTargets, formatMoneyFromMinorUnits(singlesTotalMinor + sealedTotalMinor));
      setTextContent(totalCountLabelTargets, itemCountLabel(totalItems));

      if (summaryColumn) {
        summaryColumn.hidden = totalItems === 0;
      }

      if (mobileSummary) {
        mobileSummary.hidden = totalItems === 0;
      }

      setButtonDisabledState(bulkAddButtons, availableItems === 0);
    }

    function renderGroupList(listElement, groupItems) {
      if (!listElement) {
        return;
      }

      listElement.innerHTML = '<div class="tcghive-wishlist__entries">' + groupItems.map(renderWishlistEntry).join('') + '</div>';
    }

    function renderGroups(items) {
      var singlesItems = items.filter(function (item) {
        return item.kind !== 'sealed';
      });
      var sealedItems = items.filter(function (item) {
        return item.kind === 'sealed';
      });

      if (singlesGroup) {
        singlesGroup.hidden = singlesItems.length === 0;
        renderGroupList(singlesList, singlesItems);

        if (singlesItems.length > 0) {
          updateGroupMeta(singlesGroup, singlesItems);
          setAccordionState(singlesGroup.querySelector('[data-wishlist-accordion-trigger]'), true);
        }
      }

      if (sealedGroup) {
        sealedGroup.hidden = sealedItems.length === 0;
        renderGroupList(sealedList, sealedItems);

        if (sealedItems.length > 0) {
          updateGroupMeta(sealedGroup, sealedItems);
          setAccordionState(sealedGroup.querySelector('[data-wishlist-accordion-trigger]'), true);
        }
      }
    }

    function showEmptyState() {
      root.setAttribute('data-wishlist-state', 'empty');

      if (emptyState) {
        emptyState.hidden = false;
      }

      if (loadingBlock) {
        loadingBlock.hidden = true;
      }

      if (singlesGroup) {
        singlesGroup.hidden = true;
      }

      if (sealedGroup) {
        sealedGroup.hidden = true;
      }

      if (summaryColumn) {
        summaryColumn.hidden = true;
      }
    }

    function showLoadedState(items) {
      root.setAttribute('data-wishlist-state', 'loaded');

      if (emptyState) {
        emptyState.hidden = true;
      }

      if (loadingBlock) {
        loadingBlock.hidden = true;
      }

      renderedItems = items.slice();
      renderGroups(renderedItems);
      updateSummary(renderedItems);
    }

    function resolveStoredItems(storedItems, fetchedProducts) {
      return storedItems.map(function (storedItem) {
        var productResult = fetchedProducts[storedItem.handle];

        if (!productResult || productResult.status !== 'fulfilled') {
          return resolveUnavailableItem(storedItem, 'missing_product');
        }

        return resolveLiveItem(storedItem, productResult.value);
      });
    }

    function renderWishlistPage() {
      var currentRenderVersion = renderVersion + 1;
      var storedItems = wishlistApi.getItems();
      var handles;

      renderVersion = currentRenderVersion;

      if (!storedItems.length) {
        showEmptyState();
        return Promise.resolve();
      }

      root.setAttribute('data-wishlist-state', 'loading');

      if (loadingBlock) {
        loadingBlock.hidden = false;
      }

      handles = storedItems.map(function (storedItem) {
        return storedItem.handle;
      });

      return Promise.all([fetchProductsByHandle(handles), fetchCartStateForWishlist()]).then(function (results) {
        var fetchedProducts = results[0];
        var cartState = results[1];
        var resolvedItems;

        if (currentRenderVersion !== renderVersion) {
          return;
        }

        resolvedItems = resolveStoredItems(storedItems, fetchedProducts);
        showLoadedState(applyCartStateToItems(resolvedItems, cartState));
      });
    }

    function refreshWishlistCartState() {
      if (!renderedItems.length) {
        return Promise.resolve();
      }

      return fetchCartStateForWishlist().then(function (cartState) {
        showLoadedState(applyCartStateToItems(renderedItems, cartState));
      });
    }

    function handleBulkAdd() {
      var availableItems = renderedItems.filter(function (item) {
        return item.canAddToCart === true && safeString(item.variantId) !== '';
      });
      var uniqueItemsMap = {};
      var uniqueItems = [];
      var skippedMaxCount = renderedItems.filter(function (item) {
        return item.status === 'cart_max_reached' && safeString(item.variantId) !== '';
      }).length;

      if (!availableItems.length || !cartFeedback || typeof cartFeedback.addItemsToCart !== 'function') {
        return;
      }

      availableItems.forEach(function (item) {
        if (!uniqueItemsMap[item.variantId]) {
          uniqueItemsMap[item.variantId] = true;
          uniqueItems.push({
            id: item.variantId,
            quantity: 1,
            maxQuantity: item.maxQuantity
          });
        }
      });

      setButtonDisabledState(bulkAddButtons, true);

      cartFeedback
        .addItemsToCart(uniqueItems)
        .then(function (result) {
          var addedCount = result && result.addedItems ? result.addedItems.length : 0;
          var failedCount = result && result.failedItems ? result.failedItems.length : 0;
          var toastType = failedCount > 0 ? 'error' : 'success';
          var summaryMessage = skippedMaxCount > 0 && failedCount === 0
            ? replaceTokens(getConfig('data-text-bulk-skipped-max', '[added] Artikel wurden hinzugef\u00fcgt. [skipped] Artikel sind bereits in maximaler Menge im Warenkorb.'), {
                added: addedCount,
                skipped: skippedMaxCount
              })
            : failedCount > 0
            ? replaceTokens(getConfig('data-text-bulk-partial', '[added] Artikel wurden hinzugef\u00fcgt. [failed] Artikel konnten nicht hinzugef\u00fcgt werden.'), {
                added: addedCount,
                failed: failedCount
              })
            : replaceTokens(getConfig('data-text-bulk-success', '[count] Artikel wurden in den Warenkorb gelegt.'), {
                count: addedCount
              });

          cartFeedback.showCartToast({
            type: toastType,
            productTitle: getConfig('data-text-bulk-add', 'Verf\u00fcgbares in den Warenkorb'),
            variantSummary: summaryMessage,
            showActions: addedCount > 0
          });
        })
        .catch(function () {
          cartFeedback.showCartToast({
            type: 'error',
            productTitle: getConfig('data-text-bulk-add', 'Verf\u00fcgbares in den Warenkorb'),
            variantSummary: getConfig('data-text-bulk-error', 'Die Artikel konnten nicht hinzugef\u00fcgt werden.'),
            showActions: false
          });
        })
        .finally(function () {
          setButtonDisabledState(bulkAddButtons, renderedItems.filter(function (item) {
            return item.canAddToCart === true && safeString(item.variantId) !== '';
          }).length === 0);
        });
    }

    root.addEventListener('click', function (event) {
      var accordionButton = event.target.closest('[data-wishlist-accordion-trigger]');
      var removeButton = event.target.closest('[data-wishlist-remove]');
      var clickedBulkAddButton = event.target.closest('[data-wishlist-bulk-add]');
      var isExpanded;

      if (accordionButton && root.contains(accordionButton)) {
        event.preventDefault();
        isExpanded = accordionButton.getAttribute('aria-expanded') === 'true';
        setAccordionState(accordionButton, !isExpanded);
        return;
      }

      if (removeButton && root.contains(removeButton)) {
        event.preventDefault();
        wishlistApi.remove(removeButton.getAttribute('data-wishlist-remove-variant-id'), {
          source: 'wishlist-page'
        });
        return;
      }

      if (clickedBulkAddButton && root.contains(clickedBulkAddButton)) {
        event.preventDefault();
        handleBulkAdd();
      }
    });

    wishlistApi.subscribe(function () {
      renderWishlistPage();
    });

    document.addEventListener('tcghive:cart:updated', function () {
      refreshWishlistCartState();
    });

    renderWishlistPage();
  }

  document.addEventListener('DOMContentLoaded', function () {
    bootWishlistPages(document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    bootWishlistPages(event.target);
  });
})();
